
// Re-export all hooks and types for easier imports
export * from './sleep-dialog-types';
export * from './useSleepDialogState';
export * from './useSleepDialogForm';
export * from './useSleepLookupData';
export * from './useSleepDialogSubmit';

// Create a composite hook that combines all the individual hooks
import { useCallback } from 'react';
import { useSleepDialogState } from './useSleepDialogState';
import { useSleepDialogForm } from './useSleepDialogForm';
import { useSleepLookupData } from './useSleepLookupData';
import { useSleepDialogSubmit } from './useSleepDialogSubmit';
import { SleepDialogProps, STEPS } from './sleep-dialog-types';
import { SleepFormData } from '../SleepFormContext';

// Use the imported STEPS constant instead of redefining it
export function useSleepDialog({
  open,
  onOpenChange,
  childId,
  activityToEdit,
  onSuccess
}: SleepDialogProps) {
  // First, call all hooks in a consistent order
  const dialogState = useSleepDialogState(activityToEdit);
  const form = useSleepDialogForm(childId, activityToEdit, dialogState.isEditing);
  const lookupData = useSleepLookupData();
  const submissionHandler = useSleepDialogSubmit();
  
  // After all hooks are called, destructure the objects
  const {
    saving,
    setSaving,
    currentStep,
    setCurrentStep,
    showConfirmation,
    setShowConfirmation,
    isEditing,
    locations,
    setLocations,
    environments,
    setEnvironments,
    onsetMethods,
    setOnsetMethods,
    sounds,
    setSounds,
    soundLevels,
    setSoundLevels,
    restfulnessRatings,
    setRestfulnessRatings,
    nextStep,
    prevStep
  } = dialogState;

  // Get selectedChild from form safely
  const selectedChild = form.selectedChild;
  
  // Create a wrapper for nextStep that passes form values
  const nextStepWithFormValues = useCallback(() => {
    const formValues = form.getValues();
    nextStep(formValues);
  }, [form, nextStep]);
  
  // Load lookup data
  const fetchLookupData = useCallback(() => {
    lookupData.loadLookupData(
      setLocations,
      setEnvironments,
      setOnsetMethods,
      setSounds,
      setSoundLevels,
      setRestfulnessRatings
    );
  }, [
    lookupData.loadLookupData,
    setLocations,
    setEnvironments,
    setOnsetMethods,
    setSounds,
    setSoundLevels,
    setRestfulnessRatings
  ]);

  // Simple reset function that resets everything to initial state
  const resetFormState = useCallback(() => {
    console.log(' [index.ts] Resetting form state after successful save');
    
    // Reset the form to its default values
    form.reset();
    
    // Reset the current step to start
    setCurrentStep('start');
    
    // Reset the confirmation dialog state
    setShowConfirmation(false);
  }, [form, setCurrentStep, setShowConfirmation]);

  // Handle form submission
  const onSubmit = async (data: SleepFormData) => {
    const success = await submissionHandler.handleSubmit(
      data,
      isEditing,
      activityToEdit,
      setSaving,
      onOpenChange,
      onSuccess,
      form, // Pass the form methods to allow resetting the form
      setCurrentStep, // Pass setCurrentStep to reset the step after submission
      setShowConfirmation // Pass setShowConfirmation to reset the confirmation dialog state
    );
    
    // Reset form state after successful submission
    if (success) {
      resetFormState();
    }
  };

  // Handle dialog close - modified to accept a parameter to match expected signature
  const handleClose = useCallback((open: boolean) => {
    if (!open) {
      // Reset the form state when closing the dialog
      resetFormState();
      onOpenChange(false);
    }
  }, [resetFormState, onOpenChange]);

  return {
    form,
    saving,
    currentStep,
    showConfirmation,
    locations,
    environments,
    onsetMethods,
    sounds,
    soundLevels,
    restfulnessRatings,
    selectedChild,
    handleClose,
    nextStep: nextStepWithFormValues,
    prevStep,
    onSubmit,
    setShowConfirmation,
    loadLookupData: fetchLookupData
  };
}
